/**
 * Sections du back-office master — source unique pour l'accueil en cartes
 * (/admin) et le menu d'en-tête (AdminNav).
 */
import {
  Activity,
  BookUser,
  Bot,
  Building2,
  KeyRound,
  Linkedin,
  type LucideIcon,
  Mail,
  Megaphone,
  Scale,
  Server,
  ShieldCheck,
  Target,
  Users,
  Wrench
} from 'lucide-react';

export interface AdminSection {
  path: string;
  title: string;
  description: string;
  icon: LucideIcon;
  /** Classes Tailwind de la pastille d'icône */
  accent: string;
  badge?: string;
  /** Clé ouvrable à un viewer (VIEWER_SECTIONS dans api/admin_auth.py) ; absente = admin seul */
  viewerKey?: string;
}

export interface AdminGroup {
  title: string;
  subtitle: string;
  sections: AdminSection[];
}

export const ADMIN_GROUPS: AdminGroup[] = [
  {
    title: 'Données',
    subtitle: 'Corpus juridiques, pipelines et qualité',
    sections: [
      {
        path: '/admin/legalflow',
        viewerKey: 'legalflow',
        title: 'LegalFlow',
        description:
          "Pipelines d'ingestion par pays : état, planning, volumes, erreurs, relance.",
        icon: Activity,
        accent: 'bg-violet-600/10 text-violet-600 dark:text-violet-300',
        badge: 'Nouveau'
      },
      {
        path: '/admin/fiches',
        viewerKey: 'fiches',
        title: 'Jurisprudence',
        description: 'Base de fiches d’arrêt : recherche, contrôle et édition.',
        icon: Scale,
        accent: 'bg-blue-600/10 text-blue-600 dark:text-blue-300'
      },
      {
        path: '/admin/rgpd-validation',
        viewerKey: 'rgpd',
        title: 'RGPD',
        description: 'Validation humaine des fiches du corpus RGPD.',
        icon: ShieldCheck,
        accent: 'bg-emerald-600/10 text-emerald-600 dark:text-emerald-300'
      }
    ]
  },
  {
    title: 'Observabilité',
    subtitle: 'Usage de l’API et du MCP',
    sections: [
      {
        path: '/admin/api-keys',
        viewerKey: 'usage',
        title: 'Logs & API',
        description: 'Clés API, logs de recherche et d’agents.',
        icon: KeyRound,
        accent: 'bg-amber-500/10 text-amber-600 dark:text-amber-300'
      }
    ]
  },
  {
    title: 'Clients & IA',
    subtitle: 'Cabinets, accès et assistants',
    sections: [
      {
        path: '/admin/tenants',
        title: 'Cabinets',
        description: 'Instances clientes : création, déploiement, marque.',
        icon: Building2,
        accent: 'bg-sky-600/10 text-sky-600 dark:text-sky-300'
      },
      {
        path: '/admin/users',
        title: 'Administrateurs',
        description: 'Comptes du back-office.',
        icon: Users,
        accent: 'bg-slate-600/10 text-slate-600 dark:text-slate-300'
      },
      {
        path: '/admin/agents',
        title: 'Agents',
        description: 'Assistants IA, prompts et outils associés.',
        icon: Bot,
        accent: 'bg-fuchsia-600/10 text-fuchsia-600 dark:text-fuchsia-300'
      },
      {
        path: '/admin/tools',
        title: 'Outils',
        description: 'Outils et intégrations disponibles pour les agents.',
        icon: Wrench,
        accent: 'bg-orange-600/10 text-orange-600 dark:text-orange-300'
      },
      {
        path: '/admin/mcp-servers',
        title: 'Serveurs MCP',
        description: 'Serveurs MCP connectés.',
        icon: Server,
        accent: 'bg-teal-600/10 text-teal-600 dark:text-teal-300'
      }
    ]
  },
  {
    title: 'Croissance',
    subtitle: 'Prospection et communication',
    sections: [
      {
        path: '/admin/prospects',
        title: 'Prospection',
        description: 'Avocats et RH détectés, suivi commercial.',
        icon: Target,
        accent: 'bg-rose-600/10 text-rose-600 dark:text-rose-300'
      },
      {
        path: '/admin/annuaire',
        title: 'Annuaire avocats',
        description:
          'CNB, Ordre de Paris et SIRENE croisés chaque matin : nouveaux inscrits, cabinets.',
        icon: BookUser,
        accent: 'bg-emerald-600/10 text-emerald-600 dark:text-emerald-300',
        badge: 'Nouveau'
      },
      {
        path: '/admin/linkedin',
        title: 'Posts LinkedIn',
        description: 'Posts du brief quotidien, prêts à copier-coller.',
        icon: Linkedin,
        accent: 'bg-sky-600/10 text-sky-600 dark:text-sky-300',
        badge: 'Nouveau'
      },
      {
        path: '/admin/broadcast',
        title: 'Broadcast',
        description: 'E-mail aux abonnés.',
        icon: Megaphone,
        accent: 'bg-indigo-600/10 text-indigo-600 dark:text-indigo-300'
      },
      {
        path: '/admin/email-queue',
        title: 'E-mails',
        description: 'File d’envoi : envoyés, en attente, en échec.',
        icon: Mail,
        accent: 'bg-cyan-600/10 text-cyan-600 dark:text-cyan-300'
      }
    ]
  }
];

export const ADMIN_SECTIONS: AdminSection[] = ADMIN_GROUPS.flatMap(
  (g) => g.sections
);

export const TENANT_SECTIONS: AdminSection[] = [
  {
    path: '/users',
    title: 'Utilisateurs',
    description: 'Gérer les utilisateurs du cabinet.',
    icon: Users,
    accent: 'bg-slate-600/10 text-slate-600 dark:text-slate-300'
  }
];

export interface StaffAccess {
  role: 'admin' | 'viewer' | null;
  /** Section visible pour ce compte */
  canSee: (section: AdminSection) => boolean;
  /** Section accessible pour ce chemin (/admin seul : toujours) */
  canOpen: (pathname: string) => boolean;
}

/** Droits back-office d'après User.metadata (role, sections) — miroir de require_section. */
export function staffAccess(
  metadata: Record<string, any> | undefined | null
): StaffAccess {
  const role =
    metadata?.role === 'admin' || metadata?.role === 'viewer'
      ? (metadata?.role as 'admin' | 'viewer')
      : null;
  const sections = new Set<string>(
    Array.isArray(metadata?.sections) ? (metadata?.sections as string[]) : []
  );
  const canSee = (s: AdminSection) =>
    role === 'admin' ||
    (role === 'viewer' && !!s.viewerKey && sections.has(s.viewerKey));
  const canOpen = (pathname: string) => {
    if (role === 'admin' || pathname === '/admin') return true;
    const section = ADMIN_SECTIONS.filter((s) =>
      pathname.startsWith(s.path)
    ).sort((a, b) => b.path.length - a.path.length)[0];
    return !!section && canSee(section);
  };
  return { role, canSee, canOpen };
}
